import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { FinanceTable } from './FinanceTable';
import { FinanceSummaryCards } from './FinanceSummaryCards';
import { FinanceTableFilters } from './FinanceTableFilters';
import { useFinanceListColumns } from '../../hooks/useFinanceListColumns';
import { FinanceFilters, FinanceFilterType, calcFinanceFiltersAmount } from 'modules/finance/filters';
import { FilterEvent } from 'components/shared/Filters';
import { SharedFinanceFilters, INITIAL_SHARED_FINANCE_FILTERS } from 'modules/finance/sharedFilters';
import { DateRangeBanner } from 'modules/finance/ui/components/DateRangeBanner';

interface Props {
  sharedFilters: SharedFinanceFilters;
  onSharedFiltersChange: (filters: SharedFinanceFilters) => void;
}

export const FinanceList = ({ sharedFilters, onSharedFiltersChange }: Props) => {
  const { columns } = useFinanceListColumns();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<
    Pick<FinanceFilters, FinanceFilterType.SALES_REP | FinanceFilterType.LOCATION>
  >({
    [FinanceFilterType.SALES_REP]: [],
    [FinanceFilterType.LOCATION]: [],
  });

  // State for aggregate metrics from the API
  const [aggregatedMetrics, setAggregatedMetrics] = useState({
    totalAftermarketProfit: 0,
    totalFIProfit: 0,
    productsSoldCount: 0,
  });

  // Derive the full filter config from shared state + local state
  const currentFilterConfig = useMemo<FinanceFilters>(
    () => ({
      [FinanceFilterType.DEAL_DATE]: sharedFilters.dateRange,
      [FinanceFilterType.DEAL_STATUS]: sharedFilters.dealStatus as FinanceFilters[FinanceFilterType.DEAL_STATUS],
      [FinanceFilterType.PAYMENT_TYPE]: sharedFilters.paymentType,
      [FinanceFilterType.SALES_REP]: localFilters[FinanceFilterType.SALES_REP],
      [FinanceFilterType.LOCATION]: localFilters[FinanceFilterType.LOCATION],
      [FinanceFilterType.DATE_TYPE]: sharedFilters.dateType,
    }),
    [sharedFilters, localFilters]
  );

  const onTableFiltersChanged = useCallback(
    (filters: Partial<FinanceFilters>, event: FilterEvent) => {
      if (event.type === 'apply') {
        onSharedFiltersChange({
          dateRange: filters[FinanceFilterType.DEAL_DATE] ?? sharedFilters.dateRange,
          dateType: filters[FinanceFilterType.DATE_TYPE] ?? sharedFilters.dateType,
          dealStatus: (filters[FinanceFilterType.DEAL_STATUS] ??
            sharedFilters.dealStatus) as SharedFinanceFilters['dealStatus'],
          paymentType: (filters[FinanceFilterType.PAYMENT_TYPE] ??
            sharedFilters.paymentType) as SharedFinanceFilters['paymentType'],
        });
        setLocalFilters({
          [FinanceFilterType.SALES_REP]: filters[FinanceFilterType.SALES_REP] ?? [],
          [FinanceFilterType.LOCATION]: filters[FinanceFilterType.LOCATION] ?? [],
        });
      }
      if (event.type === 'reset') {
        onSharedFiltersChange(INITIAL_SHARED_FINANCE_FILTERS);
        setLocalFilters({
          [FinanceFilterType.SALES_REP]: [],
          [FinanceFilterType.LOCATION]: [],
        });
      }
    },
    [sharedFilters, onSharedFiltersChange]
  );

  const handleMetricsChange = useCallback(
    (newMetrics: {
      productsSoldCount: number;
      totalAftermarketProfit: number;
      totalFinanceInsuranceProfit: number;
    }) => {
      setAggregatedMetrics({
        productsSoldCount: newMetrics.productsSoldCount,
        totalAftermarketProfit: newMetrics.totalAftermarketProfit,
        totalFIProfit: newMetrics.totalFinanceInsuranceProfit,
      });
    },
    []
  );

  const filtersAmount = calcFinanceFiltersAmount(currentFilterConfig);

  return (
    <Box>
      <FinanceSummaryCards
        totalAftermarketProfit={aggregatedMetrics.totalAftermarketProfit}
        totalFIProfit={aggregatedMetrics.totalFIProfit}
        productsSoldCount={aggregatedMetrics.productsSoldCount}
      />

      <DateRangeBanner dateRange={sharedFilters.dateRange} dateType={sharedFilters.dateType} />

      <Box mb={2}>
        <Button variant="outlined" startIcon={<FilterList />} onClick={() => setIsFiltersOpen(true)}>
          Filters {filtersAmount > 0 && `(${filtersAmount})`}
        </Button>
      </Box>

      <FinanceTable
        columns={columns}
        currentFilterConfig={currentFilterConfig}
        onAggregatedMetricsChange={handleMetricsChange}
      />

      <FinanceTableFilters
        isFiltersOpen={isFiltersOpen}
        currentFilterConfig={currentFilterConfig}
        setIsFiltersOpen={setIsFiltersOpen}
        onTableFiltersChanged={onTableFiltersChanged}
      />
    </Box>
  );
};
